
import { Component, DestroyRef, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { EntityService } from '@services/core/entity.service';
import { ModuleService } from '@services/core/module.service';
import { NgbModal, NgbNavModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '@services/core/user.service';
import { ActivatedRoute } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import { userModuleIds, userPayload } from './user-form.helper';
import { showAccessCheck } from './user-access-check.utils';

@Component({
  selector: 'app-user-management',
  templateUrl: './users-management.component.html',
  styleUrls: ["./users-management.component.scss"],
  imports: [DragDropModule, FormsModule, NgSelectModule, NgbTooltipModule, PanelComponent, ReactiveFormsModule],
  providers: [UserService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class UsersManagementComponent implements OnInit {
  // @Input() period
  // @Input() entity

  private _userService = inject(UserService)
  private _entityService = inject(EntityService)
  private _moduleService = inject(ModuleService)
  private _fb = inject(FormBuilder)
  private _modalService = inject(NgbModal)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  protected users = []
  protected search = ""
  protected roles
  protected offices
  protected entities
  protected modules = []
  protected selectedModules: string[] = []

  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: null
  }
  public closeResult = ""
  protected newUserForm = this._fb.group({
    id: [null],
    mail: ["", Validators.required],
    roleId: ["", Validators.required],
    officeId: ["", Validators.required],
    entityId: [""],
    enabled: [true],
  })



  ngOnInit(): void {
    const data = this.route.snapshot.data['usersManagementData']
    this.roles = data.roles
    this.offices = data.offices
    this.entities = data.entities
    this.modules = data.modules
  }

  open(modal, data?) {
    this.newUserForm.patchValue(data);
    this.selectedModules = userModuleIds(data);
    this.trackModal(this._modalService.open(modal, this.modalOptions));
  }

  private trackModal(ref: any) {
    ref.result.then((result) => this.closeWith(result), (reason) => this.dismissWith(reason));
  }

  private closeWith(result: any) {
    this.closeResult = `Closed with: ${result}`;
  }

  private dismissWith(reason: any) {
    this.closeResult = `${reason}`;
  }

  getRoles() {
    this._userService.getRoles().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (roles) => {
        this.roles = roles;
      },
      error: (err) => this.failLoad(err)
    });
  }

  getOffices() {
    this._entityService.getOffices().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (offices) => {
        this.offices = offices;
      },
      error: (err) => this.failLoad(err)
    });
  }

  getEntities() {
    this._entityService.getEntities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (entities) => {
        this.entities = entities;
      },
      error: (err) => this.failLoad(err)
    });
  }

  getUsers() {
    this._userService.getUsers(this.search).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (users) => {
        this.users = users;
      },
      error: (err) => this.failLoad(err)
    });
  }

  saveUser(){
    if (!this.newUserForm?.valid) return
    this.persistUser(userPayload(this.newUserForm.value, this.selectedModules));
  }

  private persistUser(payload: any) {
    this._userService.updateUser(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.afterUserSave(),
      error: (err) => this.failUserSave(err),
    });
  }

  private afterUserSave() {
    this.newUserForm.reset({ enabled: true });
    this.selectedModules = [];
    this.getUsers();
  }

  private failUserSave(err: any) {
    Swal.fire({ icon: 'error', title: 'Error', text: err?.error?.error || 'Error al guardar usuario' });
  }

  private failLoad(err: any) {
    Swal.fire({ icon: 'error', title: 'Error', text: err?.error?.error || 'Error al cargar datos' });
  }

  updateUser(user: any, enabled: boolean){
    user.enabled = enabled;
    this._userService.updateUser(user).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.getUsers(),
      error: (err) => this.failLoad(err)
    });
  }

  moveUp(user: any, index: number) {
    if (index === 0) return
    this.users[index] = this.users[index - 1]
    this.users[index - 1] = user
    this.saveUsersOrder()
  }

  moveDown(user: any, index: number) {
    if (index === this.users.length - 1) return
    this.users[index] = this.users[index + 1]
    this.users[index + 1] = user
    this.saveUsersOrder()
  }

  private saveUsersOrder() {
    this._userService.reorderUsers(userOrderPayload(this.users)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getUsers()
    })
  }

  checkAccess(user: any) {
    this._userService.accessCheck(user.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      showAccessCheck(res)
    })
  }

}

function userOrderPayload(users: any[]): any[] {
  const payload: any[] = []
  for (let i = 0; i < users.length; i++) payload.push({ id: users[i].id, index: i })
  return payload
} 
